<script>
    import { onMount } from 'svelte';
    import { 
        fetchLoyaltyProgram, 
        updateLoyaltyProgram, 
        fetchCoupons, 
        createCoupon, 
        deleteCoupon, 
        fetchCustomerLoyalty,
        getCurrentStore
    } from '../services/api';
    import Icon from '../components/icons/Icon.svelte';

    export let onNotify = (msg, type) => {};

    let store = getCurrentStore();
    let isLoading = true;
    let activeTab = 'program'; // 'program', 'coupons', 'customers'

    // Estado do Programa de Fidelidade
    let targetOrders = 10;
    let minOrderValue = 30.00;
    let rewardType = 'discount_value';
    let rewardValue = 20.00;
    let programActive = true;
    let isSavingProgram = false;

    // Estado dos Cupons
    let coupons = [];
    let showNewCouponModal = false;
    let newCouponCode = '';
    let newCouponType = 'fixed';
    let newCouponValue = 10.00;
    let newCouponMin = 40.00;
    let newCouponMaxUses = 100;
    let isCreatingCoupon = false;

    // Estado dos Clientes Fiéis
    let customers = [];

    function formatCurrency(val) {
        const num = parseFloat(val);
        return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
    }

    onMount(async () => {
        await loadAllData();
    });

    async function loadAllData() {
        isLoading = true;
        try {
            const [prog, coupList, custList] = await Promise.allSettled([
                fetchLoyaltyProgram(),
                fetchCoupons(),
                fetchCustomerLoyalty()
            ]);

            if (prog.status === 'fulfilled' && prog.value) {
                targetOrders = parseInt(prog.value.target_orders || 10, 10);
                minOrderValue = parseFloat(prog.value.min_order_value) || 30.00;
                rewardType = prog.value.reward_type || 'discount_value';
                rewardValue = parseFloat(prog.value.reward_value) || 20.00;
                programActive = Boolean(prog.value.active);
            }

            if (coupList.status === 'fulfilled') {
                coupons = coupList.value || [];
            }

            if (custList.status === 'fulfilled') {
                customers = custList.value || [];
            }
        } catch (err) {
            console.error('Erro ao carregar dados de fidelidade:', err);
            coupons = [
                { id: 'c1', code: 'PRIMEIRACOMPRA', discount_type: 'fixed', discount_value: 15.00, min_subtotal: 50.00, used_count: 24, max_uses: 200, active: 1 },
                { id: 'c2', code: 'VOLTA10', discount_type: 'percentage', discount_value: 10, min_subtotal: 35.00, used_count: 42, max_uses: 500, active: 1 },
                { id: 'c3', code: 'SEXTOU20', discount_type: 'fixed', discount_value: 20.00, min_subtotal: 80.00, used_count: 15, max_uses: 50, active: 1 }
            ];
            customers = [
                { id: 'cust-1', customer_name: 'Guilherme Siqueira', customer_phone: '11987654321', completed_orders: 12, total_spent: 684.50 },
                { id: 'cust-2', customer_name: 'Camila Albuquerque', customer_phone: '11977778888', completed_orders: 9, total_spent: 495.20 },
                { id: 'cust-3', customer_name: 'Fernando Rocha', customer_phone: '11999990000', completed_orders: 6, total_spent: 310.00 },
                { id: 'cust-4', customer_name: 'Mariana Lima', customer_phone: '11981112233', completed_orders: 4, total_spent: 198.80 }
            ];
        } finally {
            isLoading = false;
        }
    }

    async function handleSaveProgram() {
        isSavingProgram = true;
        try {
            await updateLoyaltyProgram({
                target_orders: parseInt(targetOrders, 10),
                min_order_value: parseFloat(minOrderValue),
                reward_type: rewardType,
                reward_value: parseFloat(rewardValue),
                active: programActive ? 1 : 0
            });
            onNotify('Regra de fidelidade salva com sucesso!', 'success');
        } catch (err) {
            onNotify(`Erro ao salvar regra: ${err.message}`, 'error');
        } finally {
            isSavingProgram = false;
        }
    }

    async function handleCreateCoupon() {
        if (!newCouponCode.trim()) {
            onNotify('Digite o código do cupom.', 'error');
            return;
        }

        isCreatingCoupon = true;
        try {
            const created = await createCoupon({
                code: newCouponCode.toUpperCase().trim(),
                discount_type: newCouponType,
                discount_value: parseFloat(newCouponValue),
                min_subtotal: parseFloat(newCouponMin || 0),
                max_uses: parseInt(newCouponMaxUses || 100, 10)
            });
            coupons = [created, ...coupons];
            showNewCouponModal = false;
            newCouponCode = '';
            onNotify(`Cupom ${created.code} criado com sucesso!`, 'success');
        } catch (err) {
            onNotify(`Erro ao criar cupom: ${err.message}`, 'error');
        } finally {
            isCreatingCoupon = false;
        }
    }

    async function handleDeleteCoupon(id, code) {
        if (!confirm(`Deseja realmente remover o cupom ${code}?`)) return;
        try {
            await deleteCoupon(id);
            coupons = coupons.filter(c => c.id !== id);
            onNotify(`Cupom ${code} removido com sucesso.`, 'success');
        } catch (err) {
            onNotify(`Erro ao remover: ${err.message}`, 'error');
        }
    }

    function getCustomerBadge(orders) {
        if (orders >= 10) return { label: 'Diamante', class: 'badge-diamond' };
        if (orders >= 7) return { label: 'Ouro', class: 'badge-gold' };
        if (orders >= 4) return { label: 'Prata', class: 'badge-silver' };
        return { label: 'Bronze', class: 'badge-bronze' };
    }
</script>

<div class="loyalty-view" id="loyalty-container">
    <!-- Header com Abas e Status -->
    <header class="loyalty-header">
        <div class="header-titles">
            <div class="header-badge">
                <span class="badge-dot"></span>
                <span>FIDELIDADE & CAMPANHAS</span>
            </div>
            <h1>Fidelização de Clientes</h1>
            <p class="subtitle">
                Crie recompensas para clientes recorrentes e gerencie campanhas promocionais.
            </p>
        </div>

        <!-- Navegação de 3 Abas Estilizadas -->
        <div class="tab-nav" role="tablist">
            <button 
                type="button"
                role="tab"
                aria-selected={activeTab === 'program'}
                class="tab-btn {activeTab === 'program' ? 'active' : ''}" 
                on:click={() => (activeTab = 'program')}
                id="tab-btn-program"
            >
                <Icon name="award" size={16} />
                <span>Programa de Fidelidade</span>
            </button>
            <button 
                type="button"
                role="tab"
                aria-selected={activeTab === 'coupons'}
                class="tab-btn {activeTab === 'coupons' ? 'active' : ''}" 
                on:click={() => (activeTab = 'coupons')}
                id="tab-btn-coupons"
            >
                <Icon name="tag" size={16} />
                <span>Cupons Promocionais ({coupons.length})</span>
            </button>
            <button 
                type="button"
                role="tab"
                aria-selected={activeTab === 'customers'}
                class="tab-btn {activeTab === 'customers' ? 'active' : ''}" 
                on:click={() => (activeTab = 'customers')}
                id="tab-btn-customers"
            >
                <Icon name="users" size={16} />
                <span>Ranking de Clientes ({customers.length})</span>
            </button>
        </div>
    </header>

    <!-- Conteúdo Principal -->
    <div class="loyalty-content">
        {#if isLoading}
            <div class="loading-state">
                <div class="spinner"></div>
                <p>Carregando configurações de fidelidade...</p>
            </div>
        {:else if activeTab === 'program'}
            <!-- ABA 1: PROGRAMA DE FIDELIDADE AUTOMÁTICO -->
            <div class="program-grid">
                <!-- Cartão da Regra -->
                <div class="card rule-card">
                    <div class="card-header">
                        <div class="header-icon award-icon">
                            <Icon name="award" size={22} />
                        </div>
                        <div class="header-title-group">
                            <h2>Regra do Cartão Fidelidade</h2>
                            <p>O robô do WhatsApp acompanha as compras do cliente automaticamente pelo número.</p>
                        </div>
                    </div>

                    <div class="form-body">
                        <div class="toggle-card">
                            <div class="toggle-info">
                                <span class="toggle-title">Status do Programa</span>
                                <span class="toggle-desc">Ative para acumular pedidos elegíveis nas conversas de WhatsApp.</span>
                            </div>
                            <div class="toggle-action-wrapper">
                                <label class="switch-control">
                                    <input type="checkbox" bind:checked={programActive} aria-label="Ativar ou pausar programa de fidelidade" />
                                    <span class="slider-switch"></span>
                                </label>
                                <span class="status-pill {programActive ? 'active' : 'inactive'}">
                                    <span class="status-dot"></span>
                                    {programActive ? 'Ativo (Pontuando)' : 'Pausado'}
                                </span>
                            </div>
                        </div>

                        <div class="input-grid">
                            <div class="field">
                                <label for="target-orders">Meta de Pedidos para Ganhar</label>
                                <div class="input-with-suffix">
                                    <input 
                                        id="target-orders" 
                                        type="number" 
                                        min="2" 
                                        max="50" 
                                        bind:value={targetOrders} 
                                    />
                                    <span class="suffix">pedidos</span>
                                </div>
                                <span class="hint">Ex: a cada 10 pedidos completos</span>
                            </div>

                            <div class="field">
                                <label for="min-order">Valor Mínimo do Pedido</label>
                                <div class="input-with-suffix">
                                    <span class="prefix">R$</span>
                                    <input 
                                        id="min-order" 
                                        type="number" 
                                        step="0.01" 
                                        bind:value={minOrderValue} 
                                    />
                                </div>
                                <span class="hint">Apenas pedidos acima deste valor pontuam</span>
                            </div>
                        </div>

                        <div class="reward-section">
                            <h3>Recompensa Concedida ao Bater a Meta</h3>
                            <div class="reward-types">
                                <label class="radio-card {rewardType === 'discount_value' ? 'selected' : ''}">
                                    <input type="radio" value="discount_value" bind:group={rewardType} />
                                    <div class="radio-content">
                                        <div class="radio-icon">
                                            <Icon name="dollar-sign" size={18} />
                                        </div>
                                        <div>
                                            <strong>Desconto em Dinheiro (R$)</strong>
                                            <p>Abate valor fixo no próximo pedido</p>
                                        </div>
                                    </div>
                                </label>

                                <label class="radio-card {rewardType === 'discount_percent' ? 'selected' : ''}">
                                    <input type="radio" value="discount_percent" bind:group={rewardType} />
                                    <div class="radio-content">
                                        <div class="radio-icon">
                                            <Icon name="percent" size={18} />
                                        </div>
                                        <div>
                                            <strong>Desconto em Porcentagem (%)</strong>
                                            <p>Aplica desconto percentual no total</p>
                                        </div>
                                    </div>
                                </label>
                            </div>

                            <div class="field reward-value-field">
                                <label for="reward-val">Valor da Recompensa</label>
                                <div class="input-with-suffix">
                                    <span class="prefix">{rewardType === 'discount_value' ? 'R$' : '%'}</span>
                                    <input 
                                        id="reward-val" 
                                        type="number" 
                                        step="1" 
                                        bind:value={rewardValue} 
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="card-footer">
                            <button 
                                type="button"
                                class="btn-save" 
                                on:click={handleSaveProgram} 
                                disabled={isSavingProgram}
                                id="btn-save-program"
                            >
                                {#if isSavingProgram}
                                    <span class="spinner-small"></span>
                                    <span>Salvando...</span>
                                {:else}
                                    <Icon name="save" size={16} />
                                    <span>Salvar Regra de Fidelidade</span>
                                {/if}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Preview do Cliente no WhatsApp -->
                <div class="card preview-card">
                    <div class="card-header">
                        <div class="header-icon wpp-icon">
                            <Icon name="message-square" size={22} />
                        </div>
                        <div class="header-title-group">
                            <h2>Simulação no WhatsApp</h2>
                            <p>Resposta automática do assistente de IA quando o cliente consulta o saldo de pontos.</p>
                        </div>
                    </div>

                    <!-- Smartphone Frame WhatsApp Preview -->
                    <div class="whatsapp-phone-frame">
                        <div class="wpp-phone-header">
                            <div class="wpp-avatar">
                                <Icon name="bot" size={18} color="#0F172A" />
                            </div>
                            <div class="wpp-info">
                                <span class="wpp-name">{store?.name || 'MataFome Gourmet'}</span>
                                <span class="wpp-status">online agora</span>
                            </div>
                        </div>

                        <div class="whatsapp-mockup">
                            <div class="wpp-bubble incoming">
                                <p>Boa noite! Quantos pedidos faltam pra eu ganhar meu desconto?</p>
                                <span class="time">20:14</span>
                            </div>

                            <div class="wpp-bubble outgoing bot">
                                <div class="bot-tag">MataFome AI • Fidelidade</div>
                                <p>
                                    Olá! Você já completou <strong>7 de {targetOrders} pedidos</strong> no seu cartão fidelidade! 🍔⭐
                                </p>
                                <div class="progress-box">
                                    <div class="progress-bar-bg">
                                        <div class="progress-bar-fill" style="width: 70%;"></div>
                                    </div>
                                    <span class="progress-text">Faltam apenas <strong>{Math.max(1, (parseInt(targetOrders, 10) || 10) - 7)} pedidos</strong> acima de R$ {formatCurrency(minOrderValue)} para você ganhar seu desconto de <strong>{rewardType === 'discount_value' ? `R$ ${formatCurrency(rewardValue)}` : `${rewardValue}%`}</strong>! 🎁🔥</span>
                                </div>
                                <span class="time">20:14</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        {:else if activeTab === 'coupons'}
            <!-- ABA 2: CUPONS DE DESCONTO EDITÁVEIS -->
            <div class="coupons-section">
                <div class="section-actions">
                    <div class="section-text">
                        <h3>Gestão de Cupons Promocionais</h3>
                        <p class="section-desc">
                            Crie códigos de desconto promocionais para campanhas, redes sociais ou fidelização direta.
                        </p>
                    </div>
                    <button type="button" class="btn-primary" on:click={() => (showNewCouponModal = true)} id="btn-new-coupon">
                        <Icon name="plus" size={16} />
                        <span>Criar Novo Cupom</span>
                    </button>
                </div>

                {#if coupons.length === 0}
                    <div class="empty-state-card">
                        <div class="empty-icon-box">
                            <Icon name="tag" size={32} />
                        </div>
                        <h3>Nenhum cupom ativo no momento</h3>
                        <p>Crie cupons promocionais com valores fixos ou porcentagens para atrair mais pedidos.</p>
                        <button type="button" class="btn-primary" on:click={() => (showNewCouponModal = true)}>
                            <Icon name="plus" size={16} />
                            <span>Criar Primeiro Cupom</span>
                        </button>
                    </div>
                {:else}
                    <div class="coupons-grid">
                        {#each coupons as coupon (coupon.id)}
                            <div class="coupon-card">
                                <div class="coupon-header">
                                    <span class="coupon-badge">
                                        {coupon.discount_type === 'fixed' ? `R$ ${formatCurrency(coupon.discount_value)} OFF` : `${coupon.discount_value}% OFF`}
                                    </span>
                                    <button
                                        type="button"
                                        class="btn-delete"
                                        on:click={() => handleDeleteCoupon(coupon.id, coupon.code)}
                                        title="Excluir cupom {coupon.code}"
                                        aria-label="Excluir cupom {coupon.code}"
                                    >
                                        <Icon name="trash" size={16} />
                                    </button>
                                </div>

                                <div class="coupon-code-box">
                                    <div class="ticket-icon">
                                        <Icon name="tag" size={18} />
                                    </div>
                                    <code class="coupon-code">{coupon.code}</code>
                                </div>

                                <div class="coupon-meta">
                                    <div class="meta-row">
                                        <span class="meta-label">Pedido Mínimo:</span>
                                        <span class="meta-val">R$ {formatCurrency(coupon.min_subtotal)}</span>
                                    </div>
                                    <div class="meta-row">
                                        <span class="meta-label">Usos Computados:</span>
                                        <span class="meta-val">{coupon.used_count || 0} / {coupon.max_uses || '∞'}</span>
                                    </div>
                                </div>

                                <div class="coupon-progress" title="Progresso de utilização">
                                    <div
                                        class="bar-fill"
                                        style="width: {Math.min(100, Math.max(0, ((parseInt(coupon.used_count, 10) || 0) / (parseInt(coupon.max_uses, 10) || 100)) * 100))}%;"
                                    ></div>
                                </div>
                            </div>
                        {/each}
                    </div>
                {/if}
            </div>

        {:else if activeTab === 'customers'}
            <!-- ABA 3: RANKING DE CLIENTES FIÉIS -->
            <div class="customers-section">
                <div class="table-card">
                    <div class="table-header">
                        <div class="table-title-group">
                            <div class="header-icon award-icon small">
                                <Icon name="award" size={18} />
                            </div>
                            <h2>Ranking dos Clientes Mais Recorrentes</h2>
                        </div>
                        <span class="count-tag">{customers.length} clientes registrados</span>
                    </div>

                    {#if customers.length === 0}
                        <div class="empty-state-card inner-empty">
                            <div class="empty-icon-box">
                                <Icon name="users" size={32} />
                            </div>
                            <h3>Nenhum cliente registrado ainda</h3>
                            <p>Conforme os clientes realizarem pedidos e concluírem entregas, eles aparecerão ranqueados aqui.</p>
                        </div>
                    {:else}
                        <div class="table-responsive">
                            <table class="data-table">
                                <thead>
                                    <tr>
                                        <th>Posição</th>
                                        <th>Cliente</th>
                                        <th>WhatsApp</th>
                                        <th>Nível de Fidelidade</th>
                                        <th>Pedidos Concluídos</th>
                                        <th>Total Consumido</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each customers as cust, index (cust.id || index)}
                                        {@const badge = getCustomerBadge(cust.completed_orders)}
                                        <tr>
                                            <td class="pos-cell">#{index + 1}</td>
                                            <td class="name-cell">
                                                <strong>{cust.customer_name || 'Cliente WhatsApp'}</strong>
                                            </td>
                                            <td class="phone-cell">{cust.customer_phone}</td>
                                            <td>
                                                <span class="cust-badge {badge.class}">
                                                    <span class="badge-dot"></span>
                                                    {badge.label}
                                                </span>
                                            </td>
                                            <td class="orders-cell">
                                                <span class="orders-pill">{cust.completed_orders} pedidos</span>
                                            </td>
                                            <td class="spent-cell">
                                                R$ {formatCurrency(cust.total_spent)}
                                            </td>
                                        </tr>
                                    {/each}
                                </tbody>
                            </table>
                        </div>
                    {/if}
                </div>
            </div>
        {/if}
    </div>

    <!-- MODAL DE NOVO CUPOM -->
    {#if showNewCouponModal}
        <div class="modal-overlay" on:click|self={() => (showNewCouponModal = false)}>
            <div class="modal-card">
                <div class="modal-header">
                    <div class="modal-title-group">
                        <Icon name="tag" size={20} />
                        <h3>Criar Novo Cupom de Desconto</h3>
                    </div>
                    <button
                        type="button"
                        class="close-btn"
                        on:click={() => (showNewCouponModal = false)}
                        aria-label="Fechar modal de cupom"
                    >
                        <Icon name="x" size={16} />
                    </button>
                </div>

                <div class="modal-body">
                    <div class="field">
                        <label for="code">Código do Cupom (ex: PROMO10, QUEROPIZZA)</label>
                        <input 
                            id="code" 
                            type="text" 
                            placeholder="CUPOM20" 
                            bind:value={newCouponCode} 
                            style="text-transform: uppercase;"
                        />
                    </div>

                    <div class="field-row">
                        <div class="field">
                            <label for="type">Tipo de Desconto</label>
                            <select id="type" bind:value={newCouponType}>
                                <option value="fixed">Valor Fixo (R$)</option>
                                <option value="percentage">Porcentagem (%)</option>
                            </select>
                        </div>

                        <div class="field">
                            <label for="val">Valor do Desconto</label>
                            <input id="val" type="number" step="0.01" min="0" bind:value={newCouponValue} />
                        </div>
                    </div>

                    <div class="field-row">
                        <div class="field">
                            <label for="min">Subtotal Mínimo (R$)</label>
                            <input id="min" type="number" step="1" bind:value={newCouponMin} />
                        </div>

                        <div class="field">
                            <label for="max">Limite de Usos</label>
                            <input id="max" type="number" bind:value={newCouponMaxUses} />
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn-cancel" on:click={() => (showNewCouponModal = false)}>Cancelar</button>
                    <button type="button" class="btn-primary" on:click={handleCreateCoupon} disabled={isCreatingCoupon}>
                        {#if isCreatingCoupon}
                            <span class="spinner-small"></span>
                            <span>Criando...</span>
                        {:else}
                            <Icon name="check" size={16} />
                            <span>Salvar e Ativar Cupom</span>
                        {/if}
                    </button>
                </div>
            </div>
        </div>
    {/if}
</div>

<style>
    /* =========================================================================
       DESIGN SYSTEM & LAYOUT: CUPONS & PROGRAMA DE FIDELIDADE
       ========================================================================= */
    .loyalty-view {
        padding: 24px 32px 80px;
        height: 100%;
        box-sizing: border-box;
        overflow-y: auto;
        overflow-x: hidden;
        background: var(--color-neutral-50, #F8FAFC);
        color: var(--color-neutral-900, #0F172A);
        font-family: inherit;
        max-width: 1560px;
        margin: 0 auto;
        width: 100%;
    }

    .loyalty-view::-webkit-scrollbar {
        width: 6px;
    }

    .loyalty-view::-webkit-scrollbar-thumb {
        background: var(--color-neutral-300, #CBD5E1);
        border-radius: 4px;
    }

    /* HEADER */
    .loyalty-header {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 24px;
        border-bottom: 1px solid var(--color-neutral-200, #E2E8F0);
        padding-bottom: 20px;
    }

    .header-titles {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .header-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: rgba(255, 106, 0, 0.08);
        color: var(--color-brand-600, #EA580C);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.05em;
        padding: 4px 10px;
        border-radius: 9999px;
        border: 1px solid rgba(255, 106, 0, 0.2);
        width: fit-content;
        margin-bottom: 4px;
    }

    .badge-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--color-brand-500, #FF6A00);
        box-shadow: 0 0 6px rgba(255, 106, 0, 0.6);
    }

    h1 {
        font-size: 26px;
        font-weight: 800;
        margin: 0;
        color: var(--color-neutral-900, #0F172A);
        letter-spacing: -0.02em;
    }

    .subtitle {
        color: var(--color-neutral-500, #64748B);
        font-size: 14px;
        margin: 0;
        line-height: 1.5;
    }

    /* BARRA DE NAVEGAÇÃO DE 3 ABAS */
    .tab-nav {
        display: flex;
        gap: 8px;
        margin-top: 8px;
        background: var(--color-neutral-100, #F1F5F9);
        padding: 4px;
        border-radius: var(--radius-lg, 12px);
        width: fit-content;
        border: 1px solid var(--color-neutral-200, #E2E8F0);
        flex-wrap: wrap;
    }

    .tab-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: transparent;
        border: none;
        color: var(--color-neutral-600, #64748B);
        padding: 9px 18px;
        border-radius: var(--radius-md, 8px);
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        min-height: 40px;
    }

    .tab-btn:hover {
        background: rgba(255, 255, 255, 0.7);
        color: var(--color-neutral-900, #0F172A);
    }

    .tab-btn:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--color-brand-500, #FF6A00);
    }

    .tab-btn.active {
        background: var(--color-neutral-0, #FFFFFF);
        color: var(--color-neutral-900, #0F172A);
        font-weight: 700;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
    }

    /* CARDS & SPLIT GRID */
    .program-grid {
        display: grid;
        grid-template-columns: 1.25fr 1fr;
        gap: 24px;
        align-items: start;
    }

    .card {
        background: var(--color-neutral-0, #FFFFFF);
        border: 1px solid var(--color-neutral-200, #E2E8F0);
        border-radius: var(--radius-xl, 16px);
        padding: 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }

    .card:hover {
        border-color: var(--color-neutral-300, #CBD5E1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .card-header {
        display: flex;
        gap: 14px;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 16px;
        border-bottom: 1px solid var(--color-neutral-100, #F1F5F9);
    }

    .header-icon {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-md, 10px);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .award-icon {
        background: #FEF3C7;
        color: #D97706;
    }

    .award-icon.small {
        width: 32px;
        height: 32px;
        border-radius: var(--radius-sm, 8px);
    }

    .wpp-icon {
        background: #DCFCE7;
        color: #16A34A;
    }

    .header-title-group h2 {
        font-size: 17px;
        font-weight: 700;
        margin: 0;
        color: var(--color-neutral-900, #0F172A);
        line-height: 1.3;
    }

    .header-title-group p {
        font-size: 13px;
        color: var(--color-neutral-500, #64748B);
        margin: 3px 0 0 0;
        line-height: 1.4;
    }

    /* TOGGLE CARD */
    .toggle-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        background: var(--color-neutral-50, #F8FAFC);
        padding: 14px 18px;
        border-radius: var(--radius-lg, 12px);
        border: 1px solid var(--color-neutral-200, #E2E8F0);
        margin-bottom: 20px;
    }

    .toggle-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .toggle-title {
        font-size: 13px;
        font-weight: 700;
        color: var(--color-neutral-800, #1E293B);
    }

    .toggle-desc {
        font-size: 11px;
        color: var(--color-neutral-500, #64748B);
    }

    .toggle-action-wrapper {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-shrink: 0;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-weight: 700;
        font-size: 12px;
        padding: 4px 10px;
        border-radius: 9999px;
    }

    .status-pill.active {
        background: #DCFCE7;
        color: #166534;
    }

    .status-pill.inactive {
        background: var(--color-neutral-100, #F1F5F9);
        color: var(--color-neutral-600, #64748B);
    }

    .status-pill .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
    }

    /* SWITCH SLIDER CONTROL */
    .switch-control {
        position: relative;
        display: inline-block;
        width: 42px;
        height: 24px;
        flex-shrink: 0;
    }

    .switch-control input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    .slider-switch {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--color-neutral-300, #CBD5E1);
        transition: .25s ease;
        border-radius: 24px;
    }

    .slider-switch:before {
        position: absolute;
        content: "";
        height: 18px;
        width: 18px;
        left: 3px;
        bottom: 3px;
        background-color: #FFFFFF;
        transition: .25s ease;
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }

    .switch-control input:checked + .slider-switch {
        background-color: var(--color-brand-500, #FF6A00);
    }

    .switch-control input:checked + .slider-switch:before {
        transform: translateX(18px);
    }

    .switch-control:focus-within .slider-switch {
        box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.2);
    }

    /* INPUTS & FORM BODY */
    .input-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin-bottom: 20px;
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .field label {
        font-size: 13px;
        font-weight: 600;
        color: var(--color-neutral-700, #334155);
    }

    .input-with-suffix {
        display: flex;
        align-items: center;
        background: var(--color-neutral-0, #FFFFFF);
        border: 1px solid var(--color-neutral-300, #CBD5E1);
        border-radius: var(--radius-md, 10px);
        padding: 0 12px;
        transition: all 0.2s ease;
    }

    .input-with-suffix:focus-within {
        border-color: var(--color-brand-500, #FF6A00);
        box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.15);
    }

    .input-with-suffix input {
        width: 100%;
        background: transparent;
        border: none;
        color: var(--color-neutral-900, #0F172A);
        font-size: 14px;
        font-weight: 700;
        padding: 10px 4px;
        outline: none;
        font-family: inherit;
    }

    .suffix, .prefix {
        color: var(--color-neutral-500, #64748B);
        font-size: 13px;
        font-weight: 600;
    }

    .hint {
        font-size: 11px;
        color: var(--color-neutral-400, #94A3B8);
        line-height: 1.35;
    }

    /* REWARD SELECTION */
    .reward-section h3 {
        font-size: 14px;
        font-weight: 700;
        color: var(--color-neutral-800, #1E293B);
        margin: 20px 0 12px 0;
    }

    .reward-types {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .radio-card {
        background: var(--color-neutral-50, #F8FAFC);
        border: 1px solid var(--color-neutral-200, #E2E8F0);
        border-radius: var(--radius-lg, 12px);
        padding: 14px;
        cursor: pointer;
        display: flex;
        gap: 10px;
        align-items: flex-start;
        transition: all 0.2s ease;
    }

    .radio-card input {
        margin-top: 3px;
        accent-color: var(--color-brand-500, #FF6A00);
    }

    .radio-card:hover {
        border-color: var(--color-neutral-300, #CBD5E1);
        background: var(--color-neutral-100, #F1F5F9);
    }

    .radio-card.selected {
        border-color: var(--color-brand-500, #FF6A00);
        background: #FFF7ED;
    }

    .radio-content {
        display: flex;
        gap: 10px;
        align-items: flex-start;
    }

    .radio-icon {
        color: var(--color-brand-600, #EA580C);
        margin-top: 2px;
    }

    .radio-content strong {
        display: block;
        font-size: 13px;
        font-weight: 700;
        color: var(--color-neutral-900, #0F172A);
    }

    .radio-content p {
        font-size: 11px;
        color: var(--color-neutral-500, #64748B);
        margin: 2px 0 0 0;
        line-height: 1.35;
    }

    .reward-value-field {
        margin-top: 14px;
        max-width: 260px;
    }

    /* BOTÕES */
    .btn-save, .btn-primary {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: linear-gradient(135deg, #FF6A00 0%, #EA580C 100%);
        color: #FFFFFF;
        border: none;
        padding: 11px 22px;
        border-radius: var(--radius-md, 10px);
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;
        box-shadow: 0 4px 14px rgba(255, 106, 0, 0.35);
        transition: all 0.2s ease;
        min-height: 44px;
    }

    .btn-save:hover:not(:disabled), .btn-primary:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 18px rgba(255, 106, 0, 0.45);
        background: linear-gradient(135deg, #FF781F 0%, #F97316 100%);
    }

    .btn-save:focus-visible, .btn-primary:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.4);
    }

    .btn-save:disabled, .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }

    .card-footer {
        margin-top: 24px;
        padding-top: 16px;
        border-top: 1px solid var(--color-neutral-100, #F1F5F9);
    }

    /* WHATSAPP SMARTPHONE MOCKUP */
    .whatsapp-phone-frame {
        background: #ECE5DD;
        border: 8px solid #1E293B;
        border-radius: 28px;
        box-shadow: 0 16px 36px rgba(0, 0, 0, 0.12), 0 0 0 2px #475569;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .wpp-phone-header {
        background: #075E54;
        color: #FFFFFF;
        padding: 10px 14px;
        display: flex;
        align-items: center;
        gap: 10px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }

    .wpp-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #FFFFFF;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .wpp-info {
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .wpp-name {
        font-size: 13px;
        font-weight: 700;
        color: #FFFFFF;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .wpp-status {
        font-size: 10px;
        color: #A7F3D0;
    }

    .whatsapp-mockup {
        padding: 16px 14px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        background-color: #ECE5DD;
        background-image: radial-gradient(#D5CEB8 1px, transparent 1px);
        background-size: 16px 16px;
        min-height: 280px;
    }

    .wpp-bubble {
        max-width: 86%;
        padding: 8px 12px;
        border-radius: 10px;
        position: relative;
        font-size: 12.5px;
        line-height: 1.4;
        box-shadow: 0 1px 1.5px rgba(0, 0, 0, 0.15);
        word-break: break-word;
    }

    .wpp-bubble.incoming {
        background: #FFFFFF;
        color: #111B21;
        align-self: flex-start;
        border-top-left-radius: 2px;
    }

    .wpp-bubble.outgoing {
        background: #E7FFDB;
        color: #111B21;
        align-self: flex-end;
        border-top-right-radius: 2px;
    }

    .bot-tag {
        font-size: 10px;
        font-weight: 800;
        color: #059669;
        margin-bottom: 4px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .progress-box {
        background: rgba(0, 0, 0, 0.05);
        padding: 8px 10px;
        border-radius: 8px;
        margin: 8px 0 4px;
        border: 1px solid rgba(0, 0, 0, 0.06);
    }

    .progress-bar-bg {
        height: 6px;
        background: #CBD5E1;
        border-radius: 9999px;
        overflow: hidden;
        margin-bottom: 6px;
    }

    .progress-bar-fill {
        height: 100%;
        background: #10B981;
        border-radius: 9999px;
        transition: width 0.3s ease;
    }

    .progress-text {
        font-size: 11px;
        color: #334155;
        display: block;
        line-height: 1.35;
    }

    .time {
        font-size: 9.5px;
        color: #667781;
        float: right;
        margin-top: 2px;
    }

    /* CUPONS SECTION */
    .coupons-section {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .section-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: var(--color-neutral-0, #FFFFFF);
        border: 1px solid var(--color-neutral-200, #E2E8F0);
        padding: 16px 20px;
        border-radius: var(--radius-xl, 16px);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
        gap: 16px;
        flex-wrap: wrap;
    }

    .section-text h3 {
        font-size: 16px;
        font-weight: 700;
        margin: 0 0 2px 0;
        color: var(--color-neutral-900, #0F172A);
    }

    .section-desc {
        color: var(--color-neutral-500, #64748B);
        font-size: 13px;
        margin: 0;
    }

    .coupons-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
        gap: 16px;
    }

    .coupon-card {
        background: var(--color-neutral-0, #FFFFFF);
        border: 1px solid var(--color-neutral-200, #E2E8F0);
        border-radius: var(--radius-xl, 16px);
        padding: 18px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
        position: relative;
    }

    .coupon-card:hover {
        border-color: var(--color-neutral-300, #CBD5E1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .coupon-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .coupon-badge {
        background: #FFF7ED;
        color: var(--color-brand-600, #EA580C);
        font-weight: 800;
        font-size: 12px;
        padding: 4px 10px;
        border-radius: 6px;
        border: 1px solid #FED7AA;
    }

    .btn-delete {
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--color-neutral-400, #94A3B8);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 6px;
        border-radius: 6px;
        transition: all 0.2s ease;
    }

    .btn-delete:hover {
        color: #EF4444;
        background: #FEF2F2;
    }

    .btn-delete:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px #EF4444;
    }

    .coupon-code-box {
        background: var(--color-neutral-50, #F8FAFC);
        border: 1px dashed var(--color-neutral-300, #CBD5E1);
        padding: 12px;
        border-radius: var(--radius-md, 10px);
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .ticket-icon {
        color: var(--color-brand-500, #FF6A00);
        display: flex;
    }

    .coupon-code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 15px;
        font-weight: 800;
        color: var(--color-neutral-900, #0F172A);
        letter-spacing: 0.05em;
    }

    .coupon-meta {
        font-size: 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .meta-row {
        display: flex;
        justify-content: space-between;
        color: var(--color-neutral-500, #64748B);
    }

    .meta-val {
        color: var(--color-neutral-900, #0F172A);
        font-weight: 600;
    }

    .coupon-progress {
        height: 6px;
        background: var(--color-neutral-100, #F1F5F9);
        border-radius: 9999px;
        overflow: hidden;
    }

    .bar-fill {
        height: 100%;
        background: var(--color-brand-500, #FF6A00);
        border-radius: 9999px;
    }

    /* TABELA DE CLIENTES */
    .table-card {
        background: var(--color-neutral-0, #FFFFFF);
        border: 1px solid var(--color-neutral-200, #E2E8F0);
        border-radius: var(--radius-xl, 16px);
        padding: 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    }

    .table-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        flex-wrap: wrap;
        gap: 12px;
    }

    .table-title-group {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .table-header h2 {
        font-size: 17px;
        font-weight: 700;
        margin: 0;
        color: var(--color-neutral-900, #0F172A);
    }

    .count-tag {
        background: var(--color-neutral-100, #F1F5F9);
        padding: 4px 10px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        color: var(--color-neutral-600, #64748B);
    }

    .table-responsive {
        width: 100%;
        overflow-x: auto;
    }

    .data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }

    .data-table th {
        text-align: left;
        color: var(--color-neutral-600, #475569);
        padding: 12px 14px;
        background: var(--color-neutral-50, #F8FAFC);
        border-bottom: 1px solid var(--color-neutral-200, #E2E8F0);
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        white-space: nowrap;
    }

    .data-table td {
        padding: 14px;
        border-bottom: 1px solid var(--color-neutral-100, #F1F5F9);
        white-space: nowrap;
    }

    .pos-cell {
        font-weight: 800;
        color: var(--color-brand-600, #EA580C);
    }

    .cust-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 700;
    }

    .cust-badge .badge-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
    }

    .badge-diamond { background: #E0F2FE; color: #0284C7; }
    .badge-gold { background: #FEF3C7; color: #D97706; }
    .badge-silver { background: #F1F5F9; color: #475569; }
    .badge-bronze { background: #FFEDD5; color: #C2410C; }

    .orders-pill {
        background: #ECFDF5;
        color: #059669;
        padding: 4px 8px;
        border-radius: 4px;
        font-weight: 700;
        font-size: 12px;
    }

    .spent-cell {
        font-weight: 700;
        color: var(--color-neutral-900, #0F172A);
    }

    /* ESTADOS VAZIOS */
    .empty-state-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        background: var(--color-neutral-0, #FFFFFF);
        border: 1px dashed var(--color-neutral-300, #CBD5E1);
        border-radius: var(--radius-xl, 16px);
        text-align: center;
        gap: 12px;
    }

    .empty-state-card.inner-empty {
        border-style: none;
        padding: 40px 20px;
    }

    .empty-icon-box {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: var(--color-neutral-100, #F1F5F9);
        color: var(--color-neutral-400, #94A3B8);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 4px;
    }

    .empty-state-card h3 {
        font-size: 16px;
        font-weight: 700;
        margin: 0;
        color: var(--color-neutral-900, #0F172A);
    }

    .empty-state-card p {
        font-size: 13px;
        color: var(--color-neutral-500, #64748B);
        margin: 0 0 6px;
        max-width: 440px;
    }

    /* MODAL */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(15, 23, 42, 0.6);
        backdrop-filter: blur(3px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        padding: 16px;
    }

    .modal-card {
        background: var(--color-neutral-0, #FFFFFF);
        border: 1px solid var(--color-neutral-200, #E2E8F0);
        border-radius: var(--radius-xl, 16px);
        width: 100%;
        max-width: 480px;
        padding: 24px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .modal-title-group {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--color-brand-600, #EA580C);
    }

    .modal-header h3 {
        margin: 0;
        font-size: 17px;
        font-weight: 700;
        color: var(--color-neutral-900, #0F172A);
    }

    .close-btn {
        background: transparent;
        border: none;
        color: var(--color-neutral-400, #94A3B8);
        cursor: pointer;
        padding: 6px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
    }

    .close-btn:hover {
        background: var(--color-neutral-100, #F1F5F9);
        color: var(--color-neutral-800, #1E293B);
    }

    .close-btn:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--color-brand-500, #FF6A00);
    }

    .modal-body {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }

    .modal-body input, .modal-body select {
        background: var(--color-neutral-50, #F8FAFC);
        border: 1px solid var(--color-neutral-300, #CBD5E1);
        color: var(--color-neutral-900, #0F172A);
        padding: 10px 14px;
        border-radius: var(--radius-md, 8px);
        font-size: 13px;
        outline: none;
        font-family: inherit;
        transition: all 0.2s ease;
        box-sizing: border-box;
        width: 100%;
    }

    .modal-body input:focus, .modal-body select:focus {
        background: #FFFFFF;
        border-color: var(--color-brand-500, #FF6A00);
        box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.15);
    }

    .field-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 24px;
    }

    .btn-cancel {
        background: var(--color-neutral-0, #FFFFFF);
        border: 1px solid var(--color-neutral-300, #CBD5E1);
        color: var(--color-neutral-700, #475569);
        padding: 10px 18px;
        border-radius: var(--radius-md, 8px);
        font-weight: 600;
        cursor: pointer;
        font-size: 13px;
        transition: all 0.2s ease;
        min-height: 44px;
    }

    .btn-cancel:hover {
        background: var(--color-neutral-50, #F8FAFC);
        border-color: var(--color-neutral-400, #94A3B8);
        color: var(--color-neutral-900, #0F172A);
    }

    .btn-cancel:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--color-neutral-400, #94A3B8);
    }

    .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px;
        gap: 14px;
        color: var(--color-neutral-500, #64748B);
    }

    .spinner {
        width: 36px;
        height: 36px;
        border: 3px solid var(--color-neutral-200, #E2E8F0);
        border-top-color: var(--color-brand-500, #FF6A00);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }

    .spinner-small {
        width: 14px;
        height: 14px;
        border: 2px solid rgba(255, 255, 255, 0.4);
        border-top-color: #FFFFFF;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    /* RESPONSIVIDADE */
    @media (max-width: 1100px) {
        .program-grid {
            grid-template-columns: 1fr;
        }

        .whatsapp-phone-frame {
            max-width: 420px;
            margin: 0 auto;
        }
    }

    @media (max-width: 768px) {
        .loyalty-view {
            padding: 16px 16px 60px;
        }

        .tab-nav {
            width: 100%;
        }

        .tab-btn {
            flex: 1;
            justify-content: center;
        }

        .card {
            padding: 16px;
        }

        .input-grid {
            grid-template-columns: 1fr;
        }

        .reward-types {
            grid-template-columns: 1fr;
        }

        .field-row {
            grid-template-columns: 1fr;
        }

        .toggle-card {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
        }

        .toggle-action-wrapper {
            width: 100%;
            justify-content: space-between;
        }

        .section-actions {
            flex-direction: column;
            align-items: stretch;
        }

        .section-actions .btn-primary {
            width: 100%;
        }
    }
</style>
